import * as React from 'react';
import {createViewConfig, view} from '@sail/engine';
import {
  checkCircle,
  warningCircle,
  warning,
  info,
} from '@sail/icons/react/Icon';
import type {IconAsset} from '@sail/icons/types';
import type {View} from '@sail/engine';
import {Icon} from './Icon';

export type InlineFeedbackProps = View.IntrinsicElement<
  'div',
  {
    /**
     * Vary the type to indicate the severity of the message.
     */
    type?: 'default' | 'warning' | 'error' | 'success';
  }
>;

export const InlineFeedbackConfig = createViewConfig({
  props: {} as InlineFeedbackProps,
  name: 'InlineFeedback',
  defaults: {
    type: 'default',
  },
});

const iconMap: Record<Required<InlineFeedbackProps>['type'], IconAsset> = {
  error: warningCircle,
  warning,
  default: info,
  success: checkCircle,
};

/**
 * Let users know how to take action on unexpected events.
 */
export const InlineFeedback = InlineFeedbackConfig.createView(
  ({children, type = 'default', ...props}) => {
    return (
      <view.div role="alert" {...props}>
        <Icon icon={iconMap[type]} size="xsmall" aria-hidden />
        <view.div>{children}</view.div>
      </view.div>
    );
  },
  {
    css: {font: 'body.small', stack: 'x', gap: 'small', textWrap: 'pretty'},
    forward: {type: true},
    variants: {
      type: {
        default: {
          css: {
            color: 'feedback.info',
          },
        },
        success: {
          css: {
            color: 'feedback.success',
          },
        },
        warning: {
          css: {
            color: 'feedback.attention',
          },
        },
        error: {
          css: {
            color: 'feedback.critical',
          },
        },
      },
    },
  },
);
